import React, { useMemo, useState } from 'react'
import {
  BAR_CHART_DEFAULT_TITLE,
  BAR_CHART_DEFAULT_VALUE_LABEL,
  BAR_CHART_VARIANT_LABELS,
  BAR_CHART_VARIANTS,
  ChartFrame,
  GroupedVerticalBar,
  SingleHorizontalBar,
  SingleVerticalBar,
  StackedHorizontalBar,
  StackedVerticalBar,
  type BarChartBar,
  type BarChartSegment,
  type BarChartVariant,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './BarChartShowcase.css'

const SAMPLE_SINGLE_VERTICAL = [
  { title: 'Mon', value: 42 },
  { title: 'Tue', value: 68 },
  { title: 'Wed', value: 55 },
  { title: 'Thu', value: 88 },
  { title: 'Fri', value: 72 },
  { title: 'Sat', value: 35 },
]

const SAMPLE_DOUBLE_VERTICAL: Array<{
  title: string
  bars: BarChartBar[]
}> = [
  {
    title: 'Mon',
    bars: [
      { value: 38, color: 'var(--ds-blue-400)', label: 'Previous' },
      { value: 52, color: 'var(--ds-blue-600)', label: 'Current' },
    ],
  },
  {
    title: 'Tue',
    bars: [
      { value: 55, color: 'var(--ds-blue-400)', label: 'Previous' },
      { value: 68, color: 'var(--ds-blue-600)', label: 'Current' },
    ],
  },
  {
    title: 'Wed',
    bars: [
      { value: 48, color: 'var(--ds-blue-400)', label: 'Previous' },
      { value: 44, color: 'var(--ds-blue-600)', label: 'Current' },
    ],
  },
  {
    title: 'Thu',
    bars: [
      { value: 72, color: 'var(--ds-blue-400)', label: 'Previous' },
      { value: 88, color: 'var(--ds-blue-600)', label: 'Current' },
    ],
  },
  {
    title: 'Fri',
    bars: [
      { value: 61, color: 'var(--ds-blue-400)', label: 'Previous' },
      { value: 79, color: 'var(--ds-blue-600)', label: 'Current' },
    ],
  },
  {
    title: 'Sat',
    bars: [
      { value: 28, color: 'var(--ds-blue-400)', label: 'Previous' },
      { value: 35, color: 'var(--ds-blue-600)', label: 'Current' },
    ],
  },
]

const DOUBLE_LEGEND = [
  { label: 'Previous', color: 'var(--ds-blue-400)' },
  { label: 'Current', color: 'var(--ds-blue-600)' },
]

const SAMPLE_STACKED_VERTICAL: Array<{
  title: string
  segments: BarChartSegment[]
}> = [
  {
    title: 'Mon',
    segments: [
      { value: 2, color: 'var(--ds-blue-300)', label: 'New' },
      { value: 3, color: 'var(--ds-blue-400)', label: 'Hygiene' },
      { value: 2, color: 'var(--ds-blue-500)', label: 'Restorative' },
      { value: 4, color: 'var(--ds-blue-600)', label: 'Other' },
    ],
  },
  {
    title: 'Tue',
    segments: [
      { value: 1, color: 'var(--ds-blue-300)', label: 'New' },
      { value: 2, color: 'var(--ds-blue-400)', label: 'Hygiene' },
      { value: 4, color: 'var(--ds-blue-500)', label: 'Restorative' },
      { value: 3, color: 'var(--ds-blue-600)', label: 'Other' },
    ],
  },
  {
    title: 'Wed',
    segments: [
      { value: 3, color: 'var(--ds-blue-300)', label: 'New' },
      { value: 2, color: 'var(--ds-blue-400)', label: 'Hygiene' },
      { value: 3, color: 'var(--ds-blue-500)', label: 'Restorative' },
      { value: 2, color: 'var(--ds-blue-600)', label: 'Other' },
    ],
  },
  {
    title: 'Thu',
    segments: [
      { value: 2, color: 'var(--ds-blue-300)', label: 'New' },
      { value: 4, color: 'var(--ds-blue-400)', label: 'Hygiene' },
      { value: 3, color: 'var(--ds-blue-500)', label: 'Restorative' },
      { value: 5, color: 'var(--ds-blue-600)', label: 'Other' },
    ],
  },
  {
    title: 'Fri',
    segments: [
      { value: 1, color: 'var(--ds-blue-300)', label: 'New' },
      { value: 1, color: 'var(--ds-blue-400)', label: 'Hygiene' },
      { value: 1, color: 'var(--ds-blue-500)', label: 'Restorative' },
      { value: 1, color: 'var(--ds-blue-600)', label: 'Other' },
    ],
  },
]

const SAMPLE_SINGLE_HORIZONTAL = [
  { title: 'Hygiene', value: 92, valueLabel: '92%' },
  { title: 'Restorative', value: 78, valueLabel: '78%' },
  { title: 'Ortho', value: 64, valueLabel: '64%' },
  { title: 'Surgery', value: 41, valueLabel: '41%' },
]

const SAMPLE_STACKED_HORIZONTAL: Array<{
  title: string
  valueLabel: string
  segments: BarChartSegment[]
}> = [
  {
    title: 'Week 1',
    valueLabel: '86%',
    segments: [
      { value: 2, color: 'var(--ds-purple-600)', label: 'Purple' },
      { value: 3, color: 'var(--ds-indigo-500)', label: 'Indigo' },
      { value: 2, color: 'var(--ds-blue-600)', label: 'Blue' },
      { value: 3, color: 'var(--ds-cyan-500)', label: 'Cyan' },
    ],
  },
  {
    title: 'Week 2',
    valueLabel: '74%',
    segments: [
      { value: 1, color: 'var(--ds-purple-600)', label: 'Purple' },
      { value: 2, color: 'var(--ds-indigo-500)', label: 'Indigo' },
      { value: 4, color: 'var(--ds-blue-600)', label: 'Blue' },
      { value: 2, color: 'var(--ds-cyan-500)', label: 'Cyan' },
    ],
  },
  {
    title: 'Week 3',
    valueLabel: '91%',
    segments: [
      { value: 3, color: 'var(--ds-purple-600)', label: 'Purple' },
      { value: 2, color: 'var(--ds-indigo-500)', label: 'Indigo' },
      { value: 3, color: 'var(--ds-blue-600)', label: 'Blue' },
      { value: 4, color: 'var(--ds-cyan-500)', label: 'Cyan' },
    ],
  },
]

const STACKED_LEGEND = [
  { label: 'New', color: 'var(--ds-blue-300)' },
  { label: 'Hygiene', color: 'var(--ds-blue-400)' },
  { label: 'Restorative', color: 'var(--ds-blue-500)' },
  { label: 'Other', color: 'var(--ds-blue-600)' },
]

const STACKED_HORIZONTAL_LEGEND = [
  { label: 'Purple', color: 'var(--ds-purple-600)' },
  { label: 'Indigo', color: 'var(--ds-indigo-500)' },
  { label: 'Blue', color: 'var(--ds-blue-600)' },
  { label: 'Cyan', color: 'var(--ds-cyan-500)' },
]

const isHorizontalVariant = (variant: BarChartVariant): boolean =>
  variant === 'single-horizontal' || variant === 'stacked-horizontal'

const renderPrimitive = (
  variant: BarChartVariant,
  title: string,
  valueLabel: string,
): React.ReactNode => {
  switch (variant) {
    case 'double':
      return <GroupedVerticalBar title={title} />
    case 'stacked':
      return <StackedVerticalBar title={title} />
    case 'single-horizontal':
      return <SingleHorizontalBar title={title} valueLabel={valueLabel} />
    case 'stacked-horizontal':
      return <StackedHorizontalBar title={title} valueLabel={valueLabel} />
    case 'single':
    default:
      return <SingleVerticalBar title={title} />
  }
}

const renderSamplePreview = (
  variant: BarChartVariant,
  showXAxisLabel: boolean,
  showYAxisLabel: boolean,
): React.ReactNode => {
  switch (variant) {
    case 'double':
      return (
        <ChartFrame
          plotHeight={240}
          yTicks={['100', '75', '50', '25', '0']}
          yAxisLabel="Visits"
          showYAxisLabel={showYAxisLabel}
          xLabels={SAMPLE_DOUBLE_VERTICAL.map((item) => item.title)}
          xAxisLabel="Day"
          showXAxisLabel={showXAxisLabel}
          legend={DOUBLE_LEGEND}
        >
          <div className="bar-chart-showcase__plot-bars">
            {SAMPLE_DOUBLE_VERTICAL.map((item) => (
              <GroupedVerticalBar
                key={item.title}
                title={item.title}
                hideTitle
                bars={item.bars}
              />
            ))}
          </div>
        </ChartFrame>
      )
    case 'stacked':
      return (
        <ChartFrame
          plotHeight={240}
          yTicks={['100%', '75%', '50%', '25%', '0%']}
          yAxisLabel="Share"
          showYAxisLabel={showYAxisLabel}
          xLabels={SAMPLE_STACKED_VERTICAL.map((item) => item.title)}
          xAxisLabel="Day"
          showXAxisLabel={showXAxisLabel}
          legend={STACKED_LEGEND}
        >
          <div className="bar-chart-showcase__plot-bars">
            {SAMPLE_STACKED_VERTICAL.map((item) => (
              <StackedVerticalBar
                key={item.title}
                title={item.title}
                hideTitle
                segments={item.segments}
                width={48}
              />
            ))}
          </div>
        </ChartFrame>
      )
    case 'single-horizontal':
      return (
        <ChartFrame
          yAxisLabel="Category"
          showYAxisLabel={showYAxisLabel}
          xAxisLabel="Completion"
          showXAxisLabel={showXAxisLabel}
          xLabels={['0%', '25%', '50%', '75%', '100%']}
          legend={[{ label: 'Completion', color: 'var(--ds-blue-500)' }]}
          showGrid={false}
        >
          <div className="bar-chart-showcase__sample-stack">
            {SAMPLE_SINGLE_HORIZONTAL.map((item) => (
              <SingleHorizontalBar
                key={item.title}
                title={item.title}
                value={item.value}
                valueLabel={item.valueLabel}
                trackGroupWidth={280}
              />
            ))}
          </div>
        </ChartFrame>
      )
    case 'stacked-horizontal':
      return (
        <ChartFrame
          yAxisLabel="Week"
          showYAxisLabel={showYAxisLabel}
          xAxisLabel="Mix"
          showXAxisLabel={showXAxisLabel}
          legend={STACKED_HORIZONTAL_LEGEND}
          showGrid={false}
        >
          <div className="bar-chart-showcase__sample-stack">
            {SAMPLE_STACKED_HORIZONTAL.map((item) => (
              <StackedHorizontalBar
                key={item.title}
                title={item.title}
                valueLabel={item.valueLabel}
                segments={item.segments}
                width={360}
              />
            ))}
          </div>
        </ChartFrame>
      )
    case 'single':
    default:
      return (
        <ChartFrame
          plotHeight={240}
          yTicks={['100', '75', '50', '25', '0']}
          yAxisLabel="Visits"
          showYAxisLabel={showYAxisLabel}
          xLabels={SAMPLE_SINGLE_VERTICAL.map((item) => item.title)}
          xAxisLabel="Day"
          showXAxisLabel={showXAxisLabel}
          legend={[{ label: 'Visits', color: 'var(--ds-blue-600)' }]}
        >
          <div className="bar-chart-showcase__plot-bars">
            {SAMPLE_SINGLE_VERTICAL.map((item) => (
              <SingleVerticalBar
                key={item.title}
                title={item.title}
                hideTitle
                value={item.value}
                width={48}
                tooltipLabel="Visits"
              />
            ))}
          </div>
        </ChartFrame>
      )
  }
}

const BarChartShowcase: React.FC = () => {
  const [variant, setVariant] = useState<BarChartVariant>('single')
  const [title, setTitle] = useState(BAR_CHART_DEFAULT_TITLE)
  const [valueLabel, setValueLabel] = useState(BAR_CHART_DEFAULT_VALUE_LABEL)
  const [showXAxisLabel, setShowXAxisLabel] = useState(false)
  const [showYAxisLabel, setShowYAxisLabel] = useState(false)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'bar-chart-variant',
            label: 'Variant',
            control: (
              <select
                id="bar-chart-variant"
                className="ui-field-control"
                value={variant}
                onChange={(event) =>
                  setVariant(event.target.value as BarChartVariant)
                }
              >
                {BAR_CHART_VARIANTS.map((option) => (
                  <option key={option} value={option}>
                    {BAR_CHART_VARIANT_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'bar-chart-title',
            label: 'Title',
            control: (
              <input
                id="bar-chart-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          ...(isHorizontalVariant(variant)
            ? [
                {
                  id: 'bar-chart-value',
                  label: 'Value label',
                  control: (
                    <input
                      id="bar-chart-value"
                      className="ui-field-control"
                      type="text"
                      value={valueLabel}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setValueLabel(event.target.value)
                      }
                    />
                  ),
                },
              ]
            : []),
          {
            id: 'bar-chart-x-title',
            label: 'X axis title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="bar-chart-x-title"
                  type="checkbox"
                  checked={showXAxisLabel}
                  onChange={(event) => setShowXAxisLabel(event.target.checked)}
                />
                <span>Show</span>
              </label>
            ),
          },
          {
            id: 'bar-chart-y-title',
            label: 'Y axis title',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="bar-chart-y-title"
                  type="checkbox"
                  checked={showYAxisLabel}
                  onChange={(event) => setShowYAxisLabel(event.target.checked)}
                />
                <span>Show</span>
              </label>
            ),
          },
        ],
      },
    ],
    [showXAxisLabel, showYAxisLabel, title, valueLabel, variant],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Bar chart"
        description="Vertical and horizontal bar primitives — solid or stacked fills with axis / value labels."
        stageClassName="ui-preview__stage--wide bar-chart-showcase__stage-wrap"
      >
        <div className={`bar-chart-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Sample data · {BAR_CHART_VARIANT_LABELS[variant]}
            </p>
            <div className="bar-chart-showcase__live-frame">
              {renderSamplePreview(variant, showXAxisLabel, showYAxisLabel)}
            </div>
          </section>

          <section className="bar-chart-showcase__matrix">
            <h4 className="bar-chart-showcase__section-title">Variants</h4>
            {BAR_CHART_VARIANTS.map((previewVariant) => (
              <div
                key={previewVariant}
                className={`bar-chart-showcase__row${
                  isHorizontalVariant(previewVariant)
                    ? ' bar-chart-showcase__row--horizontal'
                    : ''
                }${
                  previewVariant === variant
                    ? ' bar-chart-showcase__row--selected'
                    : ''
                }`}
              >
                <span className="bar-chart-showcase__row-label">
                  {BAR_CHART_VARIANT_LABELS[previewVariant]}
                </span>
                <div className="bar-chart-showcase__row-preview">
                  {renderPrimitive(previewVariant, title, valueLabel)}
                </div>
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Bar chart"
        sections={propertySections}
      />
    </>
  )
}

export default BarChartShowcase
